import { ColumnDefinition, DetailsGrid, RowDefinition, Table, Tag } from '@indigo-ag/component-module';
import { Lawsuit } from '@indigo-ag/schema';
import i18next from 'i18next';
import React, { useState } from 'react';
import { TABLE_ACTIONS, currency } from 'shared';

const EMPTY_VALUE = '-';

enum ColumnId {
  COURT = 'COURT',
  VALUE = 'VALUE',
  POLARITY = 'POLARITY',
  STATUS = 'STATUS',
  STATE = 'STATE',
  TAGS = 'TAGS'
}

interface RowShape extends Lawsuit {
  id: string;
}

const columnDefinition: ColumnDefinition<RowShape, ColumnId>[] = [
  {
    id: ColumnId.COURT,
    header: i18next.t('consultationsCard_lawsuitsTable_court'),
    cell: ({ row: { courtName }, SimpleCell }) => <SimpleCell label={courtName ?? EMPTY_VALUE} />
  },
  {
    id: ColumnId.VALUE,
    header: i18next.t('consultationsCard_lawsuitsTable_value'),
    cell: ({ row: { value }, SimpleCell }) => (
      <SimpleCell label={value != null ? currency.formatBrazilCurrency(value) : EMPTY_VALUE} />
    )
  },
  {
    id: ColumnId.POLARITY,
    header: i18next.t('consultationsCard_lawsuitsTable_polarity'),
    cell: ({ row: { polarity }, SimpleCell }) => <SimpleCell label={polarity ?? EMPTY_VALUE} />
  },
  {
    id: ColumnId.STATUS,
    header: i18next.t('consultationsCard_lawsuitsTable_status'),
    cell: ({ row: { status }, SimpleCell }) => <SimpleCell label={status ?? EMPTY_VALUE} />
  },
  {
    id: ColumnId.STATE,
    header: i18next.t('consultationsCard_lawsuitsTable_state'),
    cell: ({ row: { state }, SimpleCell }) => <SimpleCell label={state ?? EMPTY_VALUE} />
  },
  {
    id: ColumnId.TAGS,
    header: i18next.t('consultationsCard_lawsuitsTable_tags'),
    cell: ({ row: { tags } }) => <>{tags?.map(tag => <Tag key={tag} label={tag} />) ?? EMPTY_VALUE}</>
  }
];

const rowDefinition: RowDefinition<RowShape, ColumnId> = {
  rowId: ({ row: { id } }) => id,
  expandable: ({ row: { caseNumber, lastUpdate, type: lawsuitType }, ColumnSpan }) => (
    <ColumnSpan>
      <DetailsGrid
        items={[
          {
            label: i18next.t('consultationsCard_lawsuitsTable_caseNumber'),
            value: caseNumber ?? EMPTY_VALUE
          },
          {
            label: i18next.t('consultationsCard_lawsuitsTable_latestUpdate'),
            value: lastUpdate ?? EMPTY_VALUE
          },
          {
            label: i18next.t('consultationsCard_lawsuitsTable_lawsuitType'),
            value: lawsuitType ?? EMPTY_VALUE
          }
        ]}
      />
    </ColumnSpan>
  )
};

interface Props {
  lawsuits: Lawsuit[];
}

export const LawsuitsTable = ({ lawsuits }: Props) => {
  const [expandedRows, setExpandedRows] = useState<string[]>([]);

  const lawsuitsWithIds: RowShape[] = lawsuits.map((lawsuit, index) => ({
    ...lawsuit,
    id: index.toString()
  }));

  return (
    <Table
      columnDefinition={columnDefinition}
      rowDefinition={rowDefinition}
      label={''}
      data={lawsuitsWithIds}
      expandedRows={expandedRows}
      onTableAction={action => {
        switch (action.type) {
          case TABLE_ACTIONS.ROW_EXPANDED:
            return action.payload ? setExpandedRows([...expandedRows, action.payload]) : null;
          case TABLE_ACTIONS.ROW_COLLAPSED:
            return setExpandedRows(expandedRows.filter(id => id !== action.payload));
        }
      }}
    />
  );
};
